/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, {
    createContext,
    useContext,
    useEffect,
    useState,
} from 'react';
import { any } from 'prop-types';
import mbpLogger from 'mbp-logger';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import getKlarnaClientToken from '../../../../../../../../../../apis/cart-service-apis/getKlarnaClientToken';
import {
    getOrderId,
    getPaymentMethod,
    getPaymentError,
    disablePaymentFields,
} from '../../../../../../../../../../state/ducks/Checkout/ducks/Common/Common-Selectors';
import {
    getBillingAddress,
    getOrderTotalBalance,
} from '../../../../../../../../../../state/ducks/MfeCheckout/ducks/Cart/Cart-Selectors';
import { useAuth } from '../../../../../../../../../../state/ducks/Member/ducks/Auth/Plugin/auth';
import { paymentPlaceOrder } from '../../../../../../../../../../state/ducks/MfeCheckout/ducks/Payment/PlaceOrder/PlaceOrder-Actions';

import {
    KlarnaErrorFields,
    validateMandatoryFields,
    getPayloadKlarnaAuthorizePayment,
    billingFormLabel,
} from '../Helper/Helper';
import { anchorToSection, isPaymentOrReviewPage } from '../../../../Helpers/Helper';
import { getItemsDeliveryIsBeyondCutOffDateForKlarna } from '../../../../../../../../../../state/ducks/Klarna/Klarna-Selctors';
import { trackEvent } from '../../../../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { getFeatureFlag } from '../../../../../../../../../../state/ducks/Checkout/ducks/App/App-Selectors';
import usePrevious from '../../../../../../../../../gql/hooks/usePrevious';
import { getFeatureFlags, getOnePageCheckoutSaleForceABTest } from '../../../../../../../../../../state/ducks/MfeCheckout/ducks/App/App-Selectors';
import { clearOrderItemErrors, setOrderItemError } from '../../../../../../../../../../state/ducks/Checkout/ducks/Common/ducks/Error/Error-Actions';

const initialContext = {
    klarnaSessionResponse: {},
    authorizePayment: {},
    paymentContainerResponse: {},
    setPaymentContainerResponse: () => {},
    isReady: false,
    setReady: () => {},
    isScriptLoaded: false,
    setScriptLoaded: () => {},
};

const KlarnaContext = createContext(initialContext);

function useSessionStorageState(key, value) {
    const [state, setState] = useState(() => {
        const storedValue = window.sessionStorage.getItem(key);

        if (storedValue) {
            return JSON.parse(storedValue);
        }

        return value;
    });

    useEffect(() => {
        window.sessionStorage.setItem(key, JSON.stringify(state));
    }, [key, state]);

    return [state, setState];
}
function KlarnaProvider({ children }) {
    const isUseKlarnaOrganizationField = useSelector((state) => getFeatureFlag('is-use-klarna-organization-field')(state));
    const [isScriptLoaded, setScriptLoaded] = useState(() => !!document.querySelector('#klarna-payment-script'));
    const [isReady, setReady] = useState(false);
    const [paymentContainerResponse, setPaymentContainerResponse] = useState({});
    const [klarnaSessionResponse, setKlarnaSessionResponse] = useSessionStorageState('payment:klarnaSession', {});
    const [hasUserSeenCutOffError, setUserSeenCutOffError] = useSessionStorageState('payment:klarnaCutoffError', false);
    const itemsNotSupportedForDelivery = useSelector(getItemsDeliveryIsBeyondCutOffDateForKlarna);

    const selectedPaymentMethod = useSelector(getPaymentMethod);
    const billingAddress = useSelector(getBillingAddress);
    const orderTotal = useSelector(getOrderTotalBalance);
    const featureFlags = useSelector(getFeatureFlags) || {};
    const onePageCheckout = useSelector(getOnePageCheckoutSaleForceABTest) || {};

    const prevSelectedPaymentMethod = usePrevious(selectedPaymentMethod?.id);
    const prevOrderTotal = usePrevious(orderTotal);

    const history = useHistory();
    const isDisablePaymentFields = useSelector(disablePaymentFields);
    const dispatchAction = useDispatch();
    const orderId = useSelector(getOrderId);
    const paymentError = useSelector(getPaymentError);
    const { getAccessTokenSafely } = useAuth();
    let isPaymentPage = isPaymentOrReviewPage(history?.location?.pathname);

    if (
        history?.location?.pathname?.indexOf('/shipping') > 0
        && (
            featureFlags['is-one-page-checkout-enabled'] === 'newflow'
            || (
                featureFlags['is-one-page-checkout-enabled'] === 'salesforce'
                && onePageCheckout?.campaign?.campaignResponses?.[0]?.userGroup === 'Default'
            )
        )
    ) {
        isPaymentPage = true;
    }

    useEffect(() => {
        if (!hasUserSeenCutOffError && itemsNotSupportedForDelivery.length > 0) {
            dispatchAction(trackEvent({
                eventCategory: 'Checkout',
                eventAction: 'Error',
                eventLabel: 'Klarna unavailable for delivery option',
            }));

            setUserSeenCutOffError(true);
        }
    }, [hasUserSeenCutOffError, itemsNotSupportedForDelivery]);

    useEffect(() => {
        if (selectedPaymentMethod.id === 'Klarna'
            && billingAddress.country
            && billingAddress.country !== 'US') {
            dispatchAction(
                setOrderItemError(
                    orderId,
                    {
                        payment: {
                            errorKey: 'ERROR_BILLING_INPUT_MISSING',
                            errorMessage: 'Required information is missing on billing form',
                            validationErrors: {
                                country: 'Klarna payment requires US address',
                            },
                        },
                    },
                ),
            );
        } else if (
            selectedPaymentMethod.id === 'Klarna'
        ) {
            dispatchAction(
                clearOrderItemErrors(
                    orderId,
                    'payment',
                    {
                        errType: 'country',
                        field: '',
                    },
                ),
            );
        }
    }, [selectedPaymentMethod.id, billingAddress.country]);

    async function fetchKlarnaClientToken() {
        try {
            const jwtToken = await getAccessTokenSafely();
            const payload = {
                env: {},
                jwtToken,
                cartId: orderId,
            };
            const response = await getKlarnaClientToken(payload);
            setKlarnaSessionResponse(response.data);
        } catch (ex) {
            setKlarnaSessionResponse({});

            mbpLogger.logError({
                function: 'fetchKlarnaClientToken',
                appName: process.env.npm_package_name,
                module: 'mbp-checkout',
                message: 'failed fetchKlarnaClientToken klarna',
                jsError: ex,
                orderId,
            });

            dispatchAction(
                setOrderItemError(
                    orderId,
                    {
                        payment: {
                            errorKey: 'ERROR_BILLING_INPUT_MISSING',
                            errorMessage: 'We are sorry, there seems to be a technical issue with Klarna payment method, please try again later or use alternate payment method to complete order',
                        },
                    },
                ),
            );
        }
    }

    useEffect(() => {
        if ((isScriptLoaded && isPaymentPage && selectedPaymentMethod?.id === 'Klarna')
            && (!klarnaSessionResponse.client_token
                || selectedPaymentMethod?.id !== prevSelectedPaymentMethod
                || (klarnaSessionResponse.client_token && orderTotal !== prevOrderTotal))) {
            fetchKlarnaClientToken();
        }
    },
    [
        klarnaSessionResponse?.client_token,
        selectedPaymentMethod?.id,
        orderTotal,
        prevOrderTotal,
        isPaymentPage,
        isScriptLoaded,
    ]);

    useEffect(() => {
        if (isPaymentPage && selectedPaymentMethod?.id === 'Klarna' && paymentError?.error) {
            setKlarnaSessionResponse({});
        }
    }, [isPaymentPage, paymentError?.error, selectedPaymentMethod?.id]);

    const handleAuthorizationFailure = (response) => {
        const errFields = response?.error?.invalid_fields || [];
        const errorList = {};

        if (errFields.length > 0) {
            errFields.forEach((fieldError) => {
                const fieldName = KlarnaErrorFields[fieldError];

                errorList[fieldName] = `${billingFormLabel[fieldName]} is Invalid`;
            });

            dispatchAction(
                setOrderItemError(
                    orderId,
                    {
                        payment: {
                            errorKey: 'ERROR_AUTHORIZATION_FAILED_KLARNA',
                            errorMessage: 'Required information is missing on billing form',
                            validationErrors: {
                                ...errorList,
                            },
                        },
                    },
                ),
            );
        }

        if (Object.keys(errorList).length > 0 && history.location.pathname.indexOf('/review-order/') > 0) {
            history.push(`/checkout/payment/${orderId}`);
        }
    };

    const authorizePayment = () => {
        try {
            if (typeof window !== 'undefined') {
                const failedBillingFields = validateMandatoryFields(billingAddress);

                if (itemsNotSupportedForDelivery.length > 0) {
                    anchorToSection('p[class*="klarna-cutoff-error-msg"]');
                } else if (Object.keys(failedBillingFields).length === 0) {
                    const payloadAuthorizePayment = getPayloadKlarnaAuthorizePayment(orderTotal, billingAddress, klarnaSessionResponse, isUseKlarnaOrganizationField);

                    const options = {
                        payment_method_category: 'pay_over_time',
                    };

                    // additonal logging added identify missing email and phone number
                    const missingDetails = [];

                    if (!payloadAuthorizePayment?.shipping_address?.phone) {
                        missingDetails.push('shipping phone');
                    } if (!payloadAuthorizePayment?.shipping_address?.email) {
                        missingDetails.push('shipping email');
                    } if (!payloadAuthorizePayment?.billing_address?.phone) {
                        missingDetails.push('billing phone');
                    } if (!payloadAuthorizePayment?.billing_address?.email) {
                        missingDetails.push('billing email');
                    }

                    if (missingDetails.length > 0) {
                        mbpLogger.logError({
                            function: 'fetchKlarnaClientToken',
                            appName: process.env.npm_package_name,
                            module: 'mbp-checkout',
                            message: `klarna payment missing details ${missingDetails.join(',')}`,
                            orderId,
                        });
                    }

                    window.Klarna.Payments.authorize(options, payloadAuthorizePayment, (response) => {
                        if (response.approved && response.authorization_token) {
                            const placeOrderConfig = {
                                history,
                                klarnaPayment: {
                                    authorizeCode: response.authorization_token,
                                },
                                disablePaymentFields: isDisablePaymentFields,
                                source: 'PWA',
                            };

                            dispatchAction(paymentPlaceOrder(placeOrderConfig));
                        } else {
                            mbpLogger.logError({
                                function: 'window.Klarna.Payments.authorize',
                                appName: process.env.npm_package_name,
                                module: 'mbp-checkout',
                                message: 'failed to authorize klarna payment',
                                jsError: response,
                                orderId,
                            });

                            handleAuthorizationFailure(response);
                        }
                    });
                } else {
                    anchorToSection();
                    dispatchAction(
                        setOrderItemError(
                            orderId,
                            {
                                payment: {
                                    errorKey: 'ERROR_BILLING_INPUT_MISSING',
                                    errorMessage: 'Required information is missing on billing form',
                                    validationErrors: {
                                        ...failedBillingFields,
                                    },
                                },
                            },
                        ),
                    );
                }
            }
        } catch (ex) {
            mbpLogger.logError({
                function: 'authorizePayment',
                appName: process.env.npm_package_name,
                module: 'mbp-checkout',
                message: 'failed authorizePayment klarna',
                jsError: ex,
                orderId,
            });
        }
    };

    return (
        <KlarnaContext.Provider
            value={{
                klarnaSessionResponse,
                authorizePayment,
                paymentContainerResponse,
                setPaymentContainerResponse,
                isReady,
                setReady,
                isScriptLoaded,
                setScriptLoaded,
            }}
        >
            {children}
        </KlarnaContext.Provider>
    );
}

KlarnaProvider.propTypes = {
    children: any.isRequired,
};

function useKlarnaContext() {
    const context = useContext(KlarnaContext);

    return context;
}

export {
    KlarnaProvider,
    useKlarnaContext,
};
